import { evaluateCondition, visitConditionCstics } from "./condition";
import { ConfiguratorDefinitionError, type ConfiguratorDefinitionErrorContext } from "./ConfiguratorDefinitionError";
import type {
  ConfiguratorDefinition,
  ControlDefinition,
  CsticReference,
  CsticValueReference,
  CsticWriteDefinition,
  SubstepDefinition,
} from "../../contract/definition";
import type {
  ConfiguratorConfiguration,
  ConfiguratorCstic,
  ConfiguratorInstanceContext,
} from "../../contract/configuration";
import { resolveInstanceContexts } from "../instanceContext";

export interface BindingValidationOptions {
  registeredSubstepRenderers?: ReadonlySet<string>;
}

const DEFAULT_RENDERERS = new Set(["generic", "carouselColumns", "configurationAttachment"]);

const fail = (message: string, context: ConfiguratorDefinitionErrorContext): never => {
  throw new ConfiguratorDefinitionError(message, context);
};

const validateStepStructure = (definition: ConfiguratorDefinition) => {
  const seenIds = new Set<string>();
  let conditionedSuffixStarted = false;

  definition.steps.forEach((step, stepIndex) => {
    const context = { productCode: definition.productCode, stepIndex, stepId: step.id };
    if (!step.id.trim()) {
      fail("Step id must not be blank", context);
    }
    if (seenIds.has(step.id)) {
      fail("Step id must be unique", context);
    }

    const condition = step.unlockedWhen;
    if (!condition) {
      if (conditionedSuffixStarted) {
        fail("An immediately unlocked Step cannot follow a conditioned Step", context);
      }
      seenIds.add(step.id);
      return;
    }

    conditionedSuffixStarted = true;
    const prerequisites = condition.allStepsSatisfied;
    if (prerequisites.length === 0) {
      fail("allStepsSatisfied must contain at least one Step id", context);
    }
    if (new Set(prerequisites).size !== prerequisites.length) {
      fail("allStepsSatisfied must not contain duplicate Step ids", context);
    }

    prerequisites.forEach((prerequisiteId) => {
      if (prerequisiteId === step.id) {
        fail("A Step cannot depend on itself", context);
      }
      if (!seenIds.has(prerequisiteId)) {
        fail(`Unlock prerequisite '${prerequisiteId}' must reference an earlier Step`, context);
      }
    });

    const immediatePredecessor = definition.steps[stepIndex - 1];
    if (!immediatePredecessor || !prerequisites.includes(immediatePredecessor.id)) {
      fail("A conditioned Step must include its immediate predecessor", context);
    }
    seenIds.add(step.id);
  });
};

const getReferencedCstic = (
  reference: CsticReference,
  instanceContext: ConfiguratorInstanceContext,
  errorContext: ConfiguratorDefinitionErrorContext,
): ConfiguratorCstic => {
  const instance = reference.scope === "main" ? instanceContext.mainInstance : instanceContext.instance;
  const cstic = instance.cstics[reference.name];

  if (!cstic) {
    fail("Definition references a missing CSTIC", {
      ...errorContext,
      instanceId: instance.id,
      csticName: reference.name,
    });
  }

  return cstic;
};

const validateValueReference = (
  reference: CsticValueReference | CsticWriteDefinition,
  instanceContext: ConfiguratorInstanceContext,
  errorContext: ConfiguratorDefinitionErrorContext,
) => {
  const cstic = getReferencedCstic({ name: reference.cstic }, instanceContext, errorContext);

  if (reference.value === null) {
    return;
  }

  if (!cstic.values.some((value) => value.value === reference.value)) {
    fail("Definition references a value absent from the CSTIC catalog", {
      ...errorContext,
      instanceId: instanceContext.instance.id,
      csticName: reference.cstic,
    });
  }
};

const validateControl = (
  control: ControlDefinition,
  instanceContext: ConfiguratorInstanceContext,
  errorContext: ConfiguratorDefinitionErrorContext,
) => {
  getReferencedCstic({ name: control.cstic }, instanceContext, errorContext);

  if (control.helperTextCstic) {
    const helperCstic = getReferencedCstic({ name: control.helperTextCstic }, instanceContext, errorContext);
    if (!helperCstic.readOnly) {
      fail("A helperTextCstic must be read-only", {
        ...errorContext,
        instanceId: instanceContext.instance.id,
        csticName: control.helperTextCstic,
      });
    }
  }

  control.conflictCstics?.forEach((csticName) =>
    getReferencedCstic({ name: csticName }, instanceContext, errorContext),
  );

  visitConditionCstics(control.visibleWhen, (reference) =>
    getReferencedCstic(reference, instanceContext, errorContext),
  );
  visitConditionCstics(control.disabledWhen, (reference) =>
    getReferencedCstic(reference, instanceContext, errorContext),
  );

  if (control.type === "TileSelect") {
    control.tiles?.forEach((tile) => {
      tile.writes.forEach((reference) => validateValueReference(reference, instanceContext, errorContext));
      tile.selectedWhen?.forEach((reference) => validateValueReference(reference, instanceContext, errorContext));
      tile.requiresSelectable?.forEach((reference) => validateValueReference(reference, instanceContext, errorContext));
    });
  }
};

const validateSubstep = (
  definition: ConfiguratorDefinition,
  substep: SubstepDefinition,
  stepIndex: number,
  substepIndex: number,
  configuration: ConfiguratorConfiguration,
  renderers: ReadonlySet<string>,
) => {
  const renderer = substep.renderer ?? "generic";
  const baseContext = {
    productCode: definition.productCode,
    stepIndex,
    stepId: definition.steps[stepIndex]?.id,
    substepIndex,
  };

  if (!renderers.has(renderer)) {
    fail(`Substep renderer '${renderer}' is not registered`, baseContext);
  }

  if (
    substep.renderer !== "carouselColumns" &&
    substep.renderer !== "configurationAttachment" &&
    substep.controlsLayout?.type === "grid" &&
    substep.controlsLayout.columns < 1
  ) {
    fail("A grid Controls layout must define at least one column", baseContext);
  }

  if (substep.renderer === "carouselColumns" && substep.columns.length === 0) {
    fail("A carouselColumns Substep must define at least one column", baseContext);
  }

  const instanceContexts = resolveInstanceContexts(configuration, substep.instanceTarget);
  if (instanceContexts.length === 0) {
    fail("Substep instance target resolved to no instances", baseContext);
  }

  if (substep.renderer === "configurationAttachment") {
    instanceContexts.forEach((instanceContext) => {
      visitConditionCstics(substep.visibleWhen, (reference) =>
        getReferencedCstic(reference, instanceContext, baseContext),
      );
      substep.messages?.forEach((message) =>
        visitConditionCstics(message.visibleWhen, (reference) =>
          getReferencedCstic(reference, instanceContext, baseContext),
        ),
      );
    });
    return;
  }

  instanceContexts.forEach((instanceContext) => {
    visitConditionCstics(substep.visibleWhen, (reference) =>
      getReferencedCstic(reference, instanceContext, baseContext),
    );
    substep.messages?.forEach((message) =>
      visitConditionCstics(message.visibleWhen, (reference) =>
        getReferencedCstic(reference, instanceContext, baseContext),
      ),
    );
    if (substep.renderer === "carouselColumns") {
      let controlIndex = 0;
      substep.columns.forEach((column) => {
        validateControl(column.carousel, instanceContext, { ...baseContext, controlIndex });
        controlIndex += 1;

        const visibleDetails = (column.details ?? []).filter((detail) =>
          evaluateCondition(detail.visibleWhen, instanceContext),
        );
        if (visibleDetails.length > 1) {
          fail("A carousel column cannot have more than one visible detail Control", baseContext);
        }

        column.details?.forEach((detail) => {
          validateControl(detail, instanceContext, { ...baseContext, controlIndex });
          controlIndex += 1;
        });
      });
      return;
    }

    substep.controls.forEach((control, controlIndex) =>
      validateControl(control, instanceContext, { ...baseContext, controlIndex }),
    );
  });
};

/**
 * Fails once, at the page boundary, for contracts that TypeScript cannot prove against a backend
 * response. Successful binding returns the original definition so callers can compose it inline.
 */
export const bindConfiguratorDefinition = (
  definition: ConfiguratorDefinition,
  configuration: ConfiguratorConfiguration,
  options: BindingValidationOptions = {},
): ConfiguratorDefinition => {
  validateStepStructure(definition);

  if (definition.productCode !== configuration.productCode) {
    fail(`Definition does not match response product '${configuration.productCode}'`, {
      productCode: definition.productCode,
    });
  }

  const mainInstanceCount = configuration.instances.filter((instance) => instance.kind === "main").length;
  if (mainInstanceCount !== 1) {
    fail(`Response must contain exactly one main instance, received ${mainInstanceCount}`, {
      productCode: definition.productCode,
    });
  }

  const mainContexts = resolveInstanceContexts(configuration);
  const renderers = options.registeredSubstepRenderers ?? DEFAULT_RENDERERS;

  definition.steps.forEach((step, stepIndex) => {
    visitConditionCstics(step.visibleWhen, (reference) =>
      getReferencedCstic(reference, mainContexts[0], {
        productCode: definition.productCode,
        stepIndex,
        stepId: step.id,
      }),
    );
    visitConditionCstics(step.disabledWhen, (reference) =>
      getReferencedCstic(reference, mainContexts[0], {
        productCode: definition.productCode,
        stepIndex,
        stepId: step.id,
      }),
    );
    step.substeps.forEach((substep, substepIndex) =>
      validateSubstep(definition, substep, stepIndex, substepIndex, configuration, renderers),
    );
  });

  return definition;
};
